// Builds a CSS custom-property block from an assessment's resolved brand identity.
//
// Priority order (highest wins):
//   1. assessment.brand — resolved server-side from the brands + programs_branding
//      tables. This is the engine's single source of truth and what the admin
//      Brands page edits.
//   2. config.branding — legacy per-assessment escape hatch baked into the
//      config JSON. Still honoured for non-event assessments that haven't been
//      linked to a brand yet, and for fields the brand system doesn't carry
//      (custom CSS, logo dimensions, etc.).
//   3. DEFAULT_BRANDING — TSB defaults.
//
// ARCHITECTURE NOTE (May 1 2026): the taker is light-only. Branding NO LONGER
// sets bg/text/card/surface tokens — those are owned by styles.css :root.
// Branding drives identity (primary/accent), typography, and layout tokens only.

import type { Branding, ResolvedBrand } from './types';

export const DEFAULT_BRANDING: Required<Pick<Branding,
  | 'primaryColor' | 'accentColor' | 'backgroundColor'
  | 'textColor'
  | 'successColor' | 'warningColor' | 'errorColor'
  | 'fontFamily' | 'fontSizeBase' | 'fontWeightHeading'
  | 'borderRadius' | 'maxContentWidth' | 'buttonStyle' | 'progressStyle'
  | 'logoHeight'
>> = {
  primaryColor: '#0B5FFF',
  accentColor: '#FFB400',
  backgroundColor: '#0A0A2E',
  textColor: '#0A0A2E',
  successColor: '#1FB079',
  warningColor: '#F59E0B',
  errorColor: '#E5484D',
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  fontSizeBase: '16px',
  fontWeightHeading: 700,
  borderRadius: '12px',
  maxContentWidth: '720px',
  buttonStyle: 'solid',
  progressStyle: 'bar',
  logoHeight: '40px',
};

const BRANDING_STYLE_ID = 'assessment-branding-vars';
const BRAND_FONT_ID = 'assessment-brand-font';
const CONFIG_FONT_ID = 'assessment-custom-font';
const CSS_ID = 'assessment-custom-css';
const HEAD_ID = 'assessment-custom-head';

// ── Event-palette color utilities ────────────────────────────────────────────
// Lightweight in-JS color math used exclusively by the --evt-* emission block
// below. No dependencies — avoids pulling in chroma/polished for 3 functions.

/** Blend a hex color toward black. fraction=0.09 → 9% darker (used for hover). */
function evtDarken(hex: string, fraction: number): string {
  const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex;
  const ch = (idx: number) => Math.round(parseInt(m[idx], 16) * (1 - fraction));
  return `#${ch(1).toString(16).padStart(2, '0')}${ch(2).toString(16).padStart(2, '0')}${ch(3).toString(16).padStart(2, '0')}`;
}

/** Alpha-composite a hex color on white. alpha=0.06 → 6% opacity on white bg.
 *  Produces the pale/tint background variants (e.g. --tsb-coral-pale). */
function evtAlphaOnWhite(hex: string, alpha: number): string {
  const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex;
  const ch = (idx: number) =>
    Math.round(parseInt(m[idx], 16) * alpha + 255 * (1 - alpha));
  return `#${ch(1).toString(16).padStart(2, '0')}${ch(2).toString(16).padStart(2, '0')}${ch(3).toString(16).padStart(2, '0')}`;
}

/** Blend a hex color toward white. fraction=0.03 → 3% lighter (dark-2 step). */
function evtLighten(hex: string, fraction: number): string {
  const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return hex;
  const ch = (idx: number) =>
    Math.round(parseInt(m[idx], 16) * (1 - fraction) + 255 * fraction);
  return `#${ch(1).toString(16).padStart(2, '0')}${ch(2).toString(16).padStart(2, '0')}${ch(3).toString(16).padStart(2, '0')}`;
}

/** WCAG relative luminance of a hex color (0 = black, 1 = white). */
function evtLuminance(hex: string): number {
  const m = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return 0;
  const lin = (idx: number) => {
    const c = parseInt(m[idx], 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(1) + 0.7152 * lin(2) + 0.0722 * lin(3);
}

/** WCAG contrast ratio between two hex colors (1–21). */
function evtContrast(a: string, b: string): number {
  const la = evtLuminance(a);
  const lb = evtLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Pick the readable foreground for text on `bg`: white, or the brand dark,
 *  whichever has higher WCAG contrast. Guarantees a CTA on a light brand color
 *  gets dark text instead of a failing white-on-light. */
function evtReadableText(bg: string, dark: string): string {
  return evtContrast(bg, '#ffffff') >= evtContrast(bg, dark) ? '#ffffff' : dark;
}

/** Wrap a font family name in quotes if it contains whitespace. Bare CSS
 *  identifiers don't need quotes; "Open Sans" does. */
function quoteFamily(family: string): string {
  if (!family) return family;
  if (family.startsWith("'") || family.startsWith('"')) return family;
  return /\s/.test(family) ? `'${family}'` : family;
}

/** Build a CSS font-family stack from a resolved brand face name. The brand
 *  font goes first; the config-level fontFamily is preserved as fallback so
 *  the chain still works when the brand font fails to load. */
function buildFontStack(brandFamily: string | undefined, configFamily: string | undefined): string {
  const fallback = configFamily?.trim() || DEFAULT_BRANDING.fontFamily;
  if (!brandFamily) return fallback;
  return `${quoteFamily(brandFamily)}, ${fallback}`;
}

export interface ApplyBrandingOptions {
  /** Pre-resolved server-side brand bundle. Overrides config.branding tokens
   *  field-by-field when set. */
  brand?: ResolvedBrand | null;
}

export function applyBranding(
  branding: Branding | undefined,
  options: ApplyBrandingOptions = {},
): void {
  const brand = options.brand ?? null;

  // Normalize field names — configs may use short aliases (bgColor, fontBody, etc.)
  const raw: any = branding ?? {};
  const b: Branding = {
    ...raw,
    backgroundColor: raw.backgroundColor ?? raw.bgColor,
    accentColor: raw.accentColor !== raw.bgColor && raw.accentColor !== raw.backgroundColor
      ? raw.accentColor
      : raw.highlightColor ?? raw.accentColor,
    fontFamily: raw.fontFamily ?? raw.fontBody,
    fontFamilyHeading: raw.fontFamilyHeading ?? raw.fontHeading,
    fontImportUrl: raw.fontImportUrl ?? raw.googleFontsUrl,
    borderRadius: raw.borderRadius ?? raw.radius,
    maxContentWidth: raw.maxContentWidth ?? raw.maxWidth,
  };

  // ── Brand-system overrides (the new path) ─────────────────────────────
  // Both BrandPalette and ProgramPalette have a `primary` slot. BrandPalette
  // also has `cta` (accent role); ProgramPalette uses `bright` for that role.
  // Read both shapes safely.
  const palette = brand?.palette ?? {};
  const brandPrimary = palette.primary;
  const brandAccent = palette.cta ?? palette.bright ?? palette.accent;
  const brandTextDark = palette.textDark;

  // Fonts: brand fonts win over config.fontFamily; both stacks fall back to
  // the previous family / the default chain so a missing brand face still
  // degrades cleanly.
  const fontBody = buildFontStack(brand?.fonts?.body?.family, b.fontFamily);
  const fontHeading = buildFontStack(
    brand?.fonts?.heading?.family ?? brand?.fonts?.body?.family,
    b.fontFamilyHeading ?? b.fontFamily,
  );

  // Design tokens
  const radiusToken = brand?.designTokens?.radius?.button;
  const radius = radiusToken != null
    ? `${radiusToken}px`
    : (b.borderRadius ?? DEFAULT_BRANDING.borderRadius);
  const maxWidthToken = brand?.designTokens?.page?.maxWidth;
  const maxWidth = maxWidthToken != null
    ? `${maxWidthToken}px`
    : (b.maxContentWidth ?? DEFAULT_BRANDING.maxContentWidth);

  const val = (v: string | number | undefined, fallback: string | number) => String(v ?? fallback);

  // Identity tokens only — no bg/text/card/muted/border/surface writes.
  // Surface tokens are owned by styles.css :root (light-only).
  const vars = [
    `--color-primary: ${brandPrimary ?? val(b.primaryColor, DEFAULT_BRANDING.primaryColor)}`,
    `--color-accent: ${brandAccent ?? val(b.accentColor, DEFAULT_BRANDING.accentColor)}`,
    `--color-success: ${val(b.successColor, DEFAULT_BRANDING.successColor)}`,
    `--color-warning: ${val(b.warningColor, DEFAULT_BRANDING.warningColor)}`,
    `--color-error: ${val(b.errorColor, DEFAULT_BRANDING.errorColor)}`,
    `--font-body: ${fontBody}`,
    `--font-heading: ${fontHeading}`,
    `--font-size-base: ${val(b.fontSizeBase, DEFAULT_BRANDING.fontSizeBase)}`,
    `--font-weight-heading: ${val(b.fontWeightHeading, DEFAULT_BRANDING.fontWeightHeading)}`,
    `--radius: ${radius}`,
    `--max-width: ${maxWidth}`,
    `--logo-height: ${val(b.logoHeight, DEFAULT_BRANDING.logoHeight)}`,
  ];

  // Optional brand-only tokens — only emit when present so styles.css
  // fallbacks (color-mix on --color-primary) keep working otherwise.
  if (palette.tint) vars.push(`--color-tint: ${palette.tint}`);
  if (palette.canvas) vars.push(`--color-canvas: ${palette.canvas}`);
  if (brandTextDark) vars.push(`--color-text-dark: ${brandTextDark}`);
  if (brand?.fonts?.display?.family) {
    vars.push(`--font-display: ${quoteFamily(brand.fonts.display.family)}, ${fontHeading}`);
  }

  // ── Event-landing palette (--evt-*) ──────────────────────────────────────
  // Drives the .tsb-evt-* block vocabulary (taker/src/blocks-events.css).
  //
  // Emitted ONLY when a real brand resolved (never from DEFAULT_BRANDING —
  // its generic blue primary would recolour every unbranded event landing).
  //
  // Token semantics:
  //   --evt-primary / hover / pale / tint  → CTA/action color + derived shades
  //   --evt-dark / dark-2 / dark-3         → dark bg + lightened variants
  //   --evt-accent / accent-hover          → distinct highlight accent (e.g. gold)
  //                                          ONLY emitted when palette.highlight
  //                                          exists AND differs from primary —
  //                                          avoids mapping the TSB brand blue or
  //                                          hover-state colors onto gold usage.
  //
  // CSS fallbacks in blocks-events.css are the hardcoded TSB literals, so any
  // brand-linked event that has all three tokens gets a fully coherent landing;
  // gaps fall back to TSB values gracefully.
  // TSB sentinel values: when a brand's color matches these exactly, skip
  // emitting the derived variants — the hardcoded CSS fallbacks are the
  // artistically chosen TSB shades and simple lightening/darkening can't
  // reproduce them accurately (e.g. navy3 #1A1060 has specific hue/saturation
  // that a uniform lighten produces incorrectly). Brands with different colors
  // (HOA, Summit, BBC …) get full derivation; CBL stays pixel-perfect.
  const TSB_CORAL = '#ff6b35';
  const TSB_NAVY  = '#0a0a2e';

  if (brand && brandPrimary) {
    vars.push(`--evt-primary: ${brandPrimary}`);
    // Only derive variants when the brand's primary differs from TSB's — the
    // hardcoded CSS fallbacks ARE the correct TSB shades.
    if (brandPrimary.toLowerCase() !== TSB_CORAL) {
      vars.push(`--evt-primary-hover: ${evtDarken(brandPrimary, 0.09)}`);
      vars.push(`--evt-primary-pale: ${evtAlphaOnWhite(brandPrimary, 0.06)}`);
      vars.push(`--evt-primary-tint: ${evtAlphaOnWhite(brandPrimary, 0.035)}`);
      // Readable CTA text: white or the brand dark, by WCAG contrast against the
      // primary. Keeps white-on-coral for unbranded CBL (no emission → fallback),
      // but flips to dark text on light brand primaries so the CTA never fails.
      vars.push(`--evt-cta-text: ${evtReadableText(brandPrimary, brandTextDark || '#0a0a2e')}`);
    }
  }
  if (brand && brandTextDark) {
    vars.push(`--evt-dark: ${brandTextDark}`);
    // Same sentinel guard for dark variants.
    if (brandTextDark.toLowerCase() !== TSB_NAVY) {
      vars.push(`--evt-dark-2: ${evtLighten(brandTextDark, 0.03)}`);
      vars.push(`--evt-dark-3: ${evtLighten(brandTextDark, 0.12)}`);
    }
  }
  // Accent (highlight): use palette.highlight only, and only when it's a
  // distinct color from primary — prevents hover-state or identical colors
  // from overriding the TSB gold fallback.
  const brandHighlight = (palette as Record<string, unknown>).highlight as string | undefined;
  if (brand && brandHighlight && brandHighlight !== brandPrimary) {
    vars.push(`--evt-accent: ${brandHighlight}`);
    vars.push(`--evt-accent-hover: ${evtDarken(brandHighlight, 0.09)}`);
  }

  // Inject or update the <style> tag (identity tokens only — no theme overrides)
  let styleEl = document.getElementById(BRANDING_STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = BRANDING_STYLE_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = `:root {\n  ${vars.join(';\n  ')};\n}`;

  // Clean up any old inline styles that may have been set by a previous version
  // (including legacy theme-color tokens that branding used to override).
  const root = document.documentElement;
  const inlineProps = [
    '--color-primary', '--color-accent', '--color-bg', '--color-card',
    '--color-text', '--color-muted', '--color-border', '--color-success',
    '--color-warning', '--color-error', '--font-body', '--font-heading',
    '--font-size-base', '--font-weight-heading', '--radius', '--max-width',
    '--logo-height', '--bg-image', '--bg-gradient',
  ];
  for (const prop of inlineProps) {
    root.style.removeProperty(prop);
  }

  // Button / progress attribute hooks for CSS variants
  root.dataset.buttonStyle = b.buttonStyle ?? DEFAULT_BRANDING.buttonStyle;
  root.dataset.progressStyle = b.progressStyle ?? DEFAULT_BRANDING.progressStyle;

  // Inject brand Google Fonts URL when present. Prefer the brand-system URL
  // over the config-level fontImportUrl — but keep both around: a config can
  // ship a webfont the brand system doesn't carry yet (e.g. an event-specific
  // display face), and the two <link> tags are independent.
  document.getElementById(BRAND_FONT_ID)?.remove();
  if (brand?.googleFontsUrl) {
    const link = document.createElement('link');
    link.id = BRAND_FONT_ID;
    link.rel = 'stylesheet';
    link.href = brand.googleFontsUrl;
    document.head.appendChild(link);
  }

  document.getElementById(CONFIG_FONT_ID)?.remove();
  if (b.fontImportUrl && b.fontImportUrl !== brand?.googleFontsUrl) {
    const link = document.createElement('link');
    link.id = CONFIG_FONT_ID;
    link.rel = 'stylesheet';
    link.href = b.fontImportUrl;
    document.head.appendChild(link);
  }

  // Favicon
  if (b.faviconUrl) {
    let icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      document.head.appendChild(icon);
    }
    icon.href = b.faviconUrl;
  }

  // Arbitrary custom CSS escape hatch
  document.getElementById(CSS_ID)?.remove();
  if (b.customCss) {
    const style = document.createElement('style');
    style.id = CSS_ID;
    style.textContent = b.customCss;
    document.head.appendChild(style);
  }

  // Arbitrary head HTML escape hatch (tracking pixels, meta tags, etc.)
  document.getElementById(HEAD_ID)?.remove();
  if (b.customHeadHtml) {
    const container = document.createElement('div');
    container.id = HEAD_ID;
    container.style.display = 'none';
    container.innerHTML = b.customHeadHtml;
    document.head.appendChild(container);
  }

}
